import { useMemo, useState } from "react";

import intl from "react-intl-universal";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { TbTrendingUp } from "react-icons/tb";
import { Skeleton } from "@mui/material";

import { StablesPriceMiniChartSkeleton } from "../../../../Components/Molecules/DashboardSkeleton";
import { CurrencyIcon } from "../../../../Components/Atoms/CurrencyIcon";
import { useStablesPriceHistory } from "../../../../Hooks/useStablesPriceHistory";
import {
  formatMarketQuoteChange,
  formatMarketQuotePrice,
  useStablesMarketQuotes,
} from "../../../../Hooks/useStablesMarketQuotes";
import { findMarketQuoteForAsset } from "../../../../Services/stablesApi";
import { isPositiveDecimalString } from "../../../../Services/stablesMoney";
import { STABLES_SPARKLINE_WINDOWS } from "../constants";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);



const chartOptions = {

  responsive: true,

  maintainAspectRatio: false,

  interaction: { mode: "index", intersect: false },

  plugins: {

    legend: { display: false },

    tooltip: {

      backgroundColor: "#242424",

      titleFont: { size: 11, weight: "600" },

      bodyFont: { size: 11 },

      padding: 8,

      cornerRadius: 6,

      displayColors: false,

      callbacks: {

        label: (context) => formatMarketQuotePrice(context.parsed.y),

      },

    },

  },

  scales: {

    x: { display: false },

    y: { display: false },

  },

  elements: {

    line: { tension: 0.35, borderWidth: 1.5 },

    point: { radius: 0, hoverRadius: 3, hitRadius: 8 },

  },

};



export const StablesPriceMiniChart = ({
  assetId,
  marketQuotes = null,
  marketQuotesLoading = false,
}) => {
  const [sparklineWindow, setSparklineWindow] = useState("hour");

  const selectedWindow = STABLES_SPARKLINE_WINDOWS.find((option) => option.id === sparklineWindow)
    ?? STABLES_SPARKLINE_WINDOWS[0];

  const useSharedQuotes = Array.isArray(marketQuotes);
  const {
    quotes: fallbackQuotes,
    loading: fallbackQuotesLoading,
    reload: reloadQuotes,
  } = useStablesMarketQuotes({
    matchCarousel: true,
    assetId,
    includeSparklineChange: true,
    enabled: !useSharedQuotes,
  });

  const {
    history,
    loading: historyLoading,
    error: historyError,
    reload: reloadHistory,
  } = useStablesPriceHistory(assetId, sparklineWindow);

  const quote = useMemo(() => {
    if (useSharedQuotes) {
      return findMarketQuoteForAsset(marketQuotes, assetId);
    }
    return fallbackQuotes[0] ?? null;
  }, [useSharedQuotes, marketQuotes, assetId, fallbackQuotes]);

  const quotesLoading = useSharedQuotes ? marketQuotesLoading : fallbackQuotesLoading;



  const assetLabel = useMemo(() => {

    if (quote?.pair) return quote.pair.split("/")[0]?.trim() || assetId?.toUpperCase();

    return assetId?.toUpperCase() || "";

  }, [quote, assetId]);



  const displayPrice = quote?.price ?? "";
  const hasChartData = history.values?.length > 0;
  const changePercent = quote?.changePercent ?? 0;
  const isPositive = quote?.positive ?? changePercent >= 0;



  const chartData = useMemo(

    () => ({

      labels: history.labels,

      datasets: [

        {

          data: history.values,

          borderColor: "#e7a73f",

          backgroundColor: "rgba(231, 167, 63, 0.14)",

          fill: true,

          pointBackgroundColor: "#e7a73f",

        },

      ],

    }),

    [history.labels, history.values]

  );



  if (!assetId) return null;



  if (quotesLoading && historyLoading) {

    return <StablesPriceMiniChartSkeleton />;

  }



  return (

    <div className="stables-inset-panel p-3">

      <div className="flex items-start justify-between gap-2">

        <div className="flex min-w-0 items-center gap-2">

          <CurrencyIcon asset={assetId} iconUrl={quote?.iconUrl} preserveColor />

          <div className="min-w-0">

            <p className="text-[11px] font-medium text-gray-500">

              {assetLabel} / BRL

            </p>

            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">

              <span className="text-sm font-bold text-brand-dark">

                {quotesLoading ? (

                  <Skeleton variant="rounded" width={72} height={16} sx={{ display: "inline-block", borderRadius: 1 }} />

                ) : isPositiveDecimalString(displayPrice) ? (

                  formatMarketQuotePrice(displayPrice)

                ) : (

                  "—"

                )}

              </span>

              {!quotesLoading && quote && (

                <span

                  className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${

                    isPositive ? "text-emerald-600" : "text-red-500"

                  }`}

                >

                  <TbTrendingUp className={`h-3 w-3 ${isPositive ? "" : "rotate-180"}`} />

                  {formatMarketQuoteChange(changePercent, isPositive)}

                </span>

              )}

            </div>

            <p className="mt-0.5 text-[10px] text-gray-400">

              {intl.get(selectedWindow.labelKey)}

            </p>

          </div>

        </div>



        <div className="stables-segment-control shrink-0" role="group" aria-label={intl.get("stables.sparklineWindowLabel")}>

          {STABLES_SPARKLINE_WINDOWS.map((option) => {

            const isActive = option.id === sparklineWindow;

            return (

              <button

                key={option.id}

                type="button"

                aria-pressed={isActive}

                onClick={() => setSparklineWindow(option.id)}

                className={`rounded-md px-2 py-0.5 text-[10px] font-semibold transition ${

                  isActive

                    ? "bg-brand-cream text-brand-dark shadow-sm dark:bg-brand-dark-surface dark:text-gray-100"

                    : "text-gray-500 hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"

                }`}

              >

                {intl.get(option.badgeKey)}

              </button>

            );

          })}

        </div>

      </div>



      <div className="mt-2 h-14 w-full">

        {historyLoading ? (

          <Skeleton variant="rounded" height="100%" sx={{ borderRadius: 1, bgcolor: "grey.100" }} />

        ) : historyError ? (

          <div className="app-empty-state !m-0 !py-2">

            <p className="text-xs">

              {typeof historyError === "string" ? historyError : intl.get("stables.priceHistoryLoadError")}

            </p>

            <button type="button" className="btn-outline mt-2 !rounded-full !px-3 !text-xs" onClick={reloadHistory}>

              {intl.get("stables.retryLoad")}

            </button>

          </div>

        ) : hasChartData ? (

          <Line data={chartData} options={chartOptions} />

        ) : (

          <div className="app-empty-state !m-0 !py-2">

            <p className="text-xs">{intl.get("stables.priceHistoryLoadError")}</p>

            <button type="button" className="btn-outline mt-2 !rounded-full !px-3 !text-xs" onClick={reloadHistory}>

              {intl.get("stables.retryLoad")}

            </button>

          </div>

        )}

      </div>



      {!quotesLoading && !quote && (

        <button

          type="button"

          className="btn-outline mt-2 !rounded-full !px-3 !text-xs"

          onClick={reloadQuotes}

        >

          {intl.get("stables.retryLoad")}

        </button>

      )}

    </div>

  );

};

